import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FaUsers,
  FaCalendarAlt,
  FaNewspaper,
  FaImages,
  FaSpinner,
  FaDatabase,
} from "react-icons/fa";
import { MdArticle, MdPersonAdd, MdPhoto, MdEvent } from "react-icons/md";
import { GiCookingPot } from "react-icons/gi";
import {
  workshopService,
  blogService,
  mediaService,
  registrationService,
} from "../../services/firebase";
import CacheManager from "../../components/CacheManager";
import "./Dashboard.css";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([
    {
      icon: <FaCalendarAlt />,
      value: "0",
      label: t("admin.upcomingWorkshops"),
    },
    { icon: <FaNewspaper />, value: "0", label: t("admin.blogPosts") },
    { icon: <FaImages />, value: "0", label: t("admin.mediaFiles") },
    { icon: <FaUsers />, value: "0", label: t("admin.totalRegistrations") },
    { icon: <FaDatabase />, value: "0", label: "פריטי מטמון" },
  ]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "workshop":
        return <MdEvent />;
      case "blog":
        return <MdArticle />;
      case "registration":
        return <MdPersonAdd />;
      case "media":
        return <MdPhoto />;
      default:
        return <FaSpinner />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case "workshop":
        return "#8B4513";
      case "blog":
        return "#D2B48C";
      case "registration":
        return "#6B3410";
      case "media":
        return "#DEB887";
      default:
        return "#4A4A4A";
    }
  };

  const formatTimeAgo = (timestamp: any) => {
    if (!timestamp) return "תאריך לא ידוע";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return "פחות משעה";
    if (diffInHours < 24) return `${diffInHours} שעות`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} ימים`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} שבועות`;
  };

  const fetchRecentActivity = async (
    workshops: any[],
    blogPosts: any[],
    mediaFiles: any[],
    registrations: any[]
  ) => {
    const activities: any[] = [];

    // הוסף סדנאות חדשות
    workshops.slice(0, 3).forEach((workshop) => {
      if (workshop.createdAt) {
        activities.push({
          text: `${t("admin.activity.newWorkshop")}: ${workshop.title}`,
          time: formatTimeAgo(workshop.createdAt),
          type: "workshop",
          originalDate: workshop.createdAt,
        });
      }
    });

    // הוסף פוסטים חדשים
    blogPosts.slice(0, 3).forEach((post) => {
      if (post.createdAt) {
        activities.push({
          text: `${t("admin.activity.newBlogPost")}: ${post.title}`,
          time: formatTimeAgo(post.createdAt),
          type: "blog",
          originalDate: post.createdAt,
        });
      }
    });

    // הוסף הרשמות חדשות
    registrations.slice(0, 3).forEach((registration) => {
      if (registration.createdAt) {
        activities.push({
          text: `${t("admin.activity.newRegistration")}: ${
            registration.name
          } - ${registration.workshopTitle}`,
          time: formatTimeAgo(registration.createdAt),
          type: "registration",
          originalDate: registration.createdAt,
        });
      }
    });

    // הוסף קבצי מדיה חדשים
    mediaFiles.slice(0, 3).forEach((file) => {
      if (file.createdAt) {
        activities.push({
          text: `${t("admin.activity.mediaUpload")}: ${file.name}`,
          time: formatTimeAgo(file.createdAt),
          type: "media",
          originalDate: file.createdAt,
        });
      }
    });

    // מיין לפי תאריך (החדש ביותר קודם) וקח רק 5 הראשונים
    return activities
      .sort((a, b) => {
        // נשתמש בתאריך המקורי של הפריט
        const dateA = a.originalDate?.toDate
          ? a.originalDate.toDate()
          : new Date(a.originalDate);
        const dateB = b.originalDate?.toDate
          ? b.originalDate.toDate()
          : new Date(b.originalDate);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 5);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [workshops, blogPosts, mediaFiles, registrations] =
          await Promise.all([
            workshopService.getAll(),
            blogService.getAll(),
            mediaService.getAll(),
            registrationService.getAll(),
          ]);

        setStats([
          {
            icon: <FaCalendarAlt />,
            value: workshops.length.toString(),
            label: t("admin.upcomingWorkshops"),
          },
          {
            icon: <FaNewspaper />,
            value: blogPosts.length.toString(),
            label: t("admin.blogPosts"),
          },
          {
            icon: <FaImages />,
            value: mediaFiles.length.toString(),
            label: t("admin.mediaFiles"),
          },
          {
            icon: <FaUsers />,
            value: registrations.length.toString(),
            label: t("admin.totalRegistrations"),
          },
          {
            icon: <FaDatabase />,
            value: "0", // יועדכן בהמשך
            label: "פריטי מטמון",
          },
        ]);

        // קבל פעילות אחרונה
        const activity = await fetchRecentActivity(
          workshops,
          blogPosts,
          mediaFiles,
          registrations
        );
        setRecentActivity(activity);

        // עדכן ספירת פריטי מטמון
        try {
          const { enhancedApiService } = await import(
            "../../services/enhancedApiService"
          );
          const cacheStats = enhancedApiService.getCacheStats();
          setStats((prevStats) =>
            prevStats.map((stat) =>
              stat.label === "פריטי מטמון"
                ? { ...stat, value: cacheStats.totalItems.toString() }
                : stat
            )
          );
        } catch (error) {
                  }
      } catch (error) {
              } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [t]);

  if (loading) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            color: "white",
          }}
        >
          <FaSpinner
            className="fa-spin"
            style={{ fontSize: "3rem", marginBottom: "1rem" }}
          />
          <h2>{t("admin.loadingData")}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1 className="title-dashboard">{t("admin.dashboardTitle")}</h1>
      <h2 className="subtitle-dashboard">{t("admin.dashboardSubtitle")}</h2>

      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div className="stat-card" key={index}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="recent-activity">
        <h2 className="activity-title-dashboard">
          {t("admin.recentActivity")}
        </h2>
        <ul className="activity-list">
          {recentActivity.length === 0 ? (
            <li
              className="activity-item"
              style={{ textAlign: "center", color: "#718096" }}
            >
              {t("admin.noRecentActivity")}
            </li>
          ) : (
            recentActivity.map((activity, index) => (
              <li
                className="activity-item"
                key={index}
                style={{
                  borderLeftColor: getActivityColor(activity.type),
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    color: getActivityColor(activity.type),
                    fontSize: "1.2rem",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {getActivityIcon(activity.type)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "500", marginBottom: "0.25rem" }}>
                    {activity.text}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "#a0aec0" }}>
                    {activity.time}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default Dashboard;
